// A doctor's weekly clinic timings, as Doctor Settings edits them. Stored on the server as JSON,
// [{ day: 'MON', from: '15:00', to: '20:00' }, ...], one entry per day the doctor sits; the server
// (util/DoctorTimings.java) checks it and produces the printed English and Urdu lines, so the
// prescription, token slip and Display Board all read the same. This file only edits and previews.

export const DAYS = [
  { key: 'MON', label: 'Monday', short: 'Mon' },
  { key: 'TUE', label: 'Tuesday', short: 'Tue' },
  { key: 'WED', label: 'Wednesday', short: 'Wed' },
  { key: 'THU', label: 'Thursday', short: 'Thu' },
  { key: 'FRI', label: 'Friday', short: 'Fri' },
  { key: 'SAT', label: 'Saturday', short: 'Sat' },
  { key: 'SUN', label: 'Sunday', short: 'Sun' },
];

/** "15:30" -> "3:30 PM". */
export const to12h = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':').map(Number);
  const hour = h % 12 === 0 ? 12 : h % 12;
  return `${hour}:${String(m).padStart(2, '0')} ${h < 12 ? 'AM' : 'PM'}`;
};

/** Every quarter hour of the day, for the from/to pickers. */
export const TIME_OPTIONS = Array.from({ length: 96 }, (_, i) => {
  const value = `${String(Math.floor(i / 4)).padStart(2, '0')}:${String((i % 4) * 15).padStart(2, '0')}`;
  return { value, label: to12h(value) };
});

/** The stored JSON as { MON: { from, to }, ... } for the days set. Never throws. */
export const parseTimings = (stored) => {
  if (!stored) return {};
  try {
    const list = typeof stored === 'string' ? JSON.parse(stored) : stored;
    if (!Array.isArray(list)) return {};
    return Object.fromEntries(
      list
        .filter((s) => s && DAYS.some((d) => d.key === s.day))
        .map((s) => [s.day, { from: s.from, to: s.to }])
    );
  } catch {
    return {};
  }
};

/** Back to the stored shape, in week order; an empty week is '' (which clears them). */
export const serializeTimings = (byDay) => {
  const list = DAYS.filter((d) => byDay[d.key]).map((d) => ({
    day: d.key,
    from: byDay[d.key].from,
    to: byDay[d.key].to,
  }));
  return list.length ? JSON.stringify(list) : '';
};

/**
 * "Mon – Thu: 3:00 PM – 8:00 PM · Sat: 10:00 AM – 1:00 PM", the same grouping the server prints:
 * consecutive days with the same hours run together, a day off breaks the run.
 */
export const describeTimings = (byDay) => {
  const parts = [];
  let run = [];
  const flush = () => {
    if (!run.length) return;
    const first = run[0];
    const last = run[run.length - 1];
    const label = run.length === 1 ? first.short : `${first.short} – ${last.short}`;
    const t = byDay[first.key];
    parts.push(`${label}: ${to12h(t.from)} – ${to12h(t.to)}`);
    run = [];
  };
  DAYS.forEach((d) => {
    const t = byDay[d.key];
    const prev = run.length ? byDay[run[run.length - 1].key] : null;
    if (!t || (prev && (prev.from !== t.from || prev.to !== t.to))) flush();
    if (t) run.push(d);
  });
  flush();
  return parts.join(' · ');
};

/** The first problem with a week of timings, in plain words, or null. Mirrors the server's check. */
export const timingsProblem = (byDay) => {
  for (const d of DAYS) {
    const t = byDay[d.key];
    if (!t) continue;
    if (!t.from || !t.to) return `Set both times for ${d.label}`;
    if (t.to <= t.from) return `On ${d.label} the hours must end after they start`;
  }
  return null;
};
